import {$generateNodesFromDOM} from '@lexical/html';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {snackbarAtom} from 'atoms/snackbar';
import {t} from 'i18next';
import {useSetAtom} from 'jotai';
import {$insertNodes, COMMAND_PRIORITY_HIGH, PASTE_COMMAND, PasteCommandType} from 'lexical';
import {useEffect} from 'react';

/**
 * Plugin that removes table content from pasted HTML while keeping other content.
 * When a user attempts to paste content containing HTML tables,
 * the tables are stripped out but the rest of the content is pasted,
 * and a notification is shown.
 */
export const PreventTablePastePlugin = () => {
  const [editor] = useLexicalComposerContext();
  const setSnackbarMessage = useSetAtom(snackbarAtom);

  useEffect(() => {
    const removePasteListener = editor.registerCommand(
      PASTE_COMMAND,
      (event: PasteCommandType) => {
        try {
          const clipboardData = (event as ClipboardEvent).clipboardData;
          if (!clipboardData) {
            return false;
          }

          const html = clipboardData.getData('text/html');
          if (!html) {
            return false;
          }

          // Check if the HTML contains table elements
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;
          const hasTable = tempDiv.querySelector('table') !== null;

          if (hasTable) {
            // Remove all table elements from the HTML
            const tables = tempDiv.querySelectorAll('table');
            tables.forEach((table) => {
              table.remove();
            });

            // Show notification to user
            setSnackbarMessage({text: t('tables_not_supported'), accessory: '⚠️'});

            // If there's remaining content after stripping tables, insert it
            const remainingHtml = tempDiv.innerHTML.trim();
            if (remainingHtml) {
              const parser = new DOMParser();
              const dom = parser.parseFromString(remainingHtml, 'text/html');

              editor.update(() => {
                const nodes = $generateNodesFromDOM(editor, dom);
                $insertNodes(nodes);
              });
            }

            // Prevent the default paste handler from running
            return true;
          }

          return false; // No tables, let other handlers run
        } catch (error) {
          console.error('PreventTablePastePlugin error:', error);
          return false;
        }
      },
      COMMAND_PRIORITY_HIGH
    );

    return () => {
      removePasteListener();
    };
  }, [editor, setSnackbarMessage]);

  return null;
};
